import { supabase } from '../supabase';
import type { User, Profile } from '../../types';

async function signUp(email: string, password: string, userData: Partial<Profile>) {
  try {
    console.log('Signing up user with role:', userData.role);
    
    // Special case for admin@aicrm.com
    if (email === 'admin@aicrm.com') {
      userData.role = 'admin';
    }
    
    const { data, error } = await supabase.auth.signUp({
      email,
      password,
      options: {
        data: {
          full_name: userData.full_name,
          role: userData.role || 'client',
          company_name: userData.company_name,
          phone: userData.phone,
          address: userData.address,
        }
      }
    });

    if (error) throw error;

    // Don't create profile manually - let the database trigger handle it
    // The handle_new_user() trigger will create the profile automatically

    return { data, error: null };
  } catch (error) {
    console.error('Sign up error:', error);
    return { data: null, error };
  }
}

async function signIn(email: string, password: string) {
  try {
    console.log('Auth service: Signing in user:', email);
    const { data, error } = await supabase.auth.signInWithPassword({
      email,
      password,
    });

    if (error) throw error;
    
    // Log the user data for debugging
    console.log('Auth service: Sign in successful, user ID:', data.user?.id);
    
    return { data, error: null };
  } catch (error) {
    console.error('Sign in error:', error);
    return { data: null, error };
  }
}

async function signOut() {
  try {
    const { error } = await supabase.auth.signOut();
    if (error) throw error;
    return { error: null };
  } catch (error) {
    console.error('Sign out error:', error);
    return { error };
  }
}

async function getCurrentUser(): Promise<{ user: User | null; profile: Profile | null }> {
  try {
    const { data: { user }, error: userError } = await supabase.auth.getUser();
    
    if (userError) {
      // Check if this is the expected "Auth session missing!" error
      const errorMessage = userError.message;
      if (errorMessage === 'Auth session missing!') {
        // This is a normal state (no user logged in), don't log as error
        return { user: null, profile: null };
      }
      throw userError;
    }
    
    if (!user) return { user: null, profile: null };

    console.log('Auth service: Got current user, ID:', user.id, 'Email:', user.email);

    // Special case for admin@aicrm.com
    if (user.email === 'admin@aicrm.com') {
      console.log('Auth service: Special case for admin@aicrm.com');
      
      // Try to get profile
      const { data: profileData, error: profileError } = await supabase
        .from('profiles')
        .select('*')
        .eq('id', user.id)
        .single();
      
      if (profileError || !profileData || profileData.role !== 'admin') {
        console.log('Auth service: Fixing admin role for admin@aicrm.com');
        
        // Fix the role
        await fixUserRole(user.email, 'admin');
        
        // Get the updated profile
        const { data: updatedProfile } = await supabase
          .from('profiles')
          .select('*')
          .eq('id', user.id)
          .single();
        
        return { 
          user, 
          profile: updatedProfile || {
            id: user.id,
            email: user.email,
            role: 'admin',
            full_name: 'Admin User',
            created_at: new Date().toISOString(),
            updated_at: new Date().toISOString()
          } 
        };
      }
      
      return { user, profile: profileData };
    }

    // Try to get profile, but handle gracefully if it doesn't exist yet
    let profile = null;
    try {
      const { data: profileData, error: profileError } = await supabase
        .from('profiles')
        .select('*')
        .eq('id', user.id)
        .single();

      if (profileError) {
        console.warn('Profile fetch error:', profileError.message);
        // Don't try to create profile here - let the database trigger handle it
        // Just return null profile and let the app handle it gracefully
      } else {
        profile = profileData;
        console.log('Auth service: Profile fetched successfully, role:', profile.role);
      }
    } catch (profileError) {
      console.warn('Profile handling error:', profileError);
      // Continue without profile - the trigger will create it eventually
    }

    return { user, profile };
  } catch (error) {
    // Check if this is the expected "Auth session missing!" error
    const errorMessage = error instanceof Error ? error.message : String(error);
    if (errorMessage === 'Auth session missing!') {
      // This is a normal state (no user logged in), don't log as error
      return { user: null, profile: null };
    }
    
    console.error('Get current user error:', error);
    return { user: null, profile: null };
  }
}

async function updateProfile(userId: string, updates: Partial<Profile>) {
  try {
    const { data, error } = await supabase
      .from('profiles')
      .update(updates)
      .eq('id', userId)
      .select()
      .single();

    if (error) throw error;
    return { data, error: null };
  } catch (error) {
    console.error('Update profile error:', error);
    return { data: null, error };
  }
}

async function resetPassword(email: string) {
  try {
    const { error } = await supabase.auth.resetPasswordForEmail(email);
    if (error) throw error;
    return { error: null };
  } catch (error) {
    console.error('Reset password error:', error);
    return { error };
  }
}

// Function to manually fix a user's role in the database
async function fixUserRole(email: string, role: 'admin' | 'client') {
  try {
    console.log(`Fixing role for ${email} to ${role}`);
    
    // First get the user
    const { data: { user } } = await supabase.auth.getUser();
    
    if (!user) {
      throw new Error('No authenticated user found');
    }
    
    // Update user metadata
    const { error: metadataError } = await supabase.auth.updateUser({
      data: { role }
    });
    
    if (metadataError) {
      console.error('Error updating user metadata:', metadataError);
    }
    
    // Update the profile
    const { error: profileError } = await supabase
      .from('profiles')
      .update({ role })
      .eq('email', email);
    
    if (profileError) {
      console.error('Error updating profile:', profileError);
      throw profileError;
    }
    
    console.log(`Successfully fixed role for ${email} to ${role}`);
    return { success: true, error: null };
  } catch (error) {
    console.error('Fix user role error:', error);
    return { success: false, error };
  }
}

export const authService = {
  signUp,
  signIn,
  signOut,
  getCurrentUser,
  updateProfile,
  resetPassword,
  fixUserRole,
};

// Also export individual functions for backward compatibility
export { signUp, signIn, signOut, getCurrentUser, updateProfile, resetPassword, fixUserRole };